import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { createPage } from '../../../store/actions/pageActions';
import { slugify } from '../../../utils/stringUtils';
import RichTextEditor from '../../common/RichTextEditor';
import PreviewDialog from './PreviewDialog';
import { Visibility as VisibilityIcon, AutoStories as AIIcon } from '@mui/icons-material';
import AIPromptDialog from './AIPromptDialog';

function CreatePageDialog({ open, onClose, accountId, siteId }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    isPublished: false,
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isAIPromptOpen, setIsAIPromptOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'title') {
      // Auto-generate slug from title
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: slugify(value),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'isPublished' ? checked : value,
      }));
    }
  };

  const handleContentChange = (html) => {
    setFormData(prev => ({
      ...prev,
      content: html,
    }));
  };

  const handleAIContentGenerated = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await dispatch(createPage({
        accountId,
        siteId,
        pageData: {
          ...formData,
          content: formData.content || '', // Ensure content is not null
        },
      })).unwrap();
      
      onClose();
      // Reset form
      setFormData({
        title: '',
        slug: '',
        content: '',
        isPublished: false,
      });
    } catch (error) {
      setError(error?.message || 'Failed to create page');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Page</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="title"
                label="Page Title"
                value={formData.title}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                name="slug"
                label="URL Slug"
                value={formData.slug}
                onChange={handleChange}
                required
                fullWidth
                helperText="This will be the page's URL path"
              />

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Content</Typography>
                  <Button
                    size="small"
                    startIcon={<AIIcon />}
                    onClick={() => setIsAIPromptOpen(true)}
                  >
                    Generate with AI
                  </Button>
                </Box>
                <RichTextEditor
                  value={formData.content}
                  onChange={handleContentChange}
                  placeholder="Enter page content..."
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublished}
                    onChange={handleChange}
                    name="isPublished"
                  />
                }
                label="Publish page immediately"
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setIsPreviewOpen(true)}
              startIcon={<VisibilityIcon />}
            >
              Preview
            </Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              Create Page
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <AIPromptDialog
        open={isAIPromptOpen}
        onClose={() => setIsAIPromptOpen(false)}
        onContentGenerated={handleAIContentGenerated}
        accountId={accountId}
        microSiteId={siteId}
      />

      <PreviewDialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={formData.title}
        content={formData.content}
      />
    </>
  );
}

export default CreatePageDialog; 